'use client';

import React from 'react';
import { FloatButton as AntFloatButton, ConfigProvider } from 'antd';
import type { FloatButtonProps as AntFloatButtonProps } from 'antd';
import { orionTheme } from '../theme/orion-theme';
import clsx from 'clsx';

export interface FloatButtonProps extends AntFloatButtonProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * FloatButton component - Ant Design FloatButton with OpenStore Orion theming
 * 
 * This component provides FULL access to all Ant Design FloatButton props while
 * automatically applying the OpenStore Orion theme.
 * 
 * Features:
 * - Orion design specifications (colors, typography, spacing)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design FloatButton features and props
 * 
 * @example
 * ```tsx
 * // Basic float button with Orion theming
 * <FloatButton icon={<PlusOutlined />} />
 * 
 * // All Ant Design FloatButton props work
 * <FloatButton
 *   icon={<PlusOutlined />}           // ✅ Icon
 *   type="primary"                    // ✅ Type (primary/default)
 *   shape="circle"                    // ✅ Shape (circle/square)
 *   size="large"                      // ✅ Size (small/large)
 *   description="Add Product"         // ✅ Description text
 *   tooltip="Create new product"      // ✅ Tooltip
 *   href="/products/new"              // ✅ Link functionality
 *   target="_blank"                   // ✅ Link target
 *   onClick={handleClick}             // ✅ Click handler
 *   onMouseEnter={handleHover}        // ✅ Hover handler
 *   badge={{ count: 5 }}              // ✅ Badge notification
 *   className="shadow-2xl"            // ✅ Tailwind overrides
 * />
 * 
 * // Float button group
 * <FloatButton.Group
 *   trigger="click"                   // ✅ Trigger (click/hover)
 *   type="primary"                    // ✅ Group type
 *   style={{ right: 24 }}            // ✅ Positioning
 *   icon={<MoreOutlined />}           // ✅ Group icon
 *   tooltip="Quick Actions"           // ✅ Group tooltip
 *   className="custom-float-group"    // ✅ Tailwind overrides
 * >
 *   <FloatButton 
 *     icon={<PlusOutlined />} 
 *     tooltip="Add Product"
 *     onClick={() => handleAdd()}
 *   />
 *   <FloatButton 
 *     icon={<EditOutlined />} 
 *     tooltip="Edit Selected"
 *     onClick={() => handleEdit()}
 *   />
 *   <FloatButton 
 *     icon={<DeleteOutlined />} 
 *     tooltip="Delete Selected"
 *     onClick={() => handleDelete()}
 *   />
 * </FloatButton.Group>
 * 
 * // Back to top button
 * <FloatButton.BackTop
 *   style={{ right: 100 }}            // ✅ Custom positioning
 *   visibilityHeight={100}            // ✅ Visibility threshold
 *   duration={500}                    // ✅ Animation duration
 *   target={() => window}             // ✅ Scroll target
 *   onClick={() => console.log('Back to top')} // ✅ Click handler
 *   className="shadow-lg"             // ✅ Tailwind overrides
 * />
 * 
 * // Float button with badge
 * <FloatButton
 *   icon={<MessageOutlined />}
 *   badge={{ 
 *     count: unreadCount,             // ✅ Dynamic badge count
 *     overflowCount: 99,              // ✅ Overflow handling
 *     showZero: false,                // ✅ Show zero option
 *     color: '#ea4335',               // ✅ Custom badge color
 *   }}
 *   tooltip="Messages"
 *   onClick={openMessages}
 * />
 * 
 * // Custom styled float button
 * <FloatButton
 *   icon={<PlusOutlined />}
 *   type="primary"
 *   className="
 *     shadow-2xl 
 *     border-2 
 *     border-orion-300 
 *     hover:scale-110 
 *     transition-transform
 *   "
 *   style={{
 *     right: 24,
 *     bottom: 80,
 *   }}
 * />
 * ```
 */
export const FloatButton: React.FC<FloatButtonProps> = ({
  className,
  ...props // All other Ant Design props pass through
}) => {
  return (
    <ConfigProvider theme={orionTheme}>
      <AntFloatButton
        className={clsx(
          'orion-float-button',
          // Add custom classes for Tailwind overrides
          className
        )}
        {...props} // Pass through ALL Ant Design props
      />
    </ConfigProvider>
  );
};

FloatButton.displayName = 'FloatButton';

// Export all FloatButton sub-components from Ant Design
export const { 
  Group: FloatButtonGroup,
  BackTop: FloatButtonBackTop 
} = AntFloatButton;

// Attach sub-components to FloatButton for dot notation access
(FloatButton as any).Group = FloatButtonGroup;
(FloatButton as any).BackTop = FloatButtonBackTop;

// Export convenience components for common patterns
export const AddFloatButton: React.FC<Omit<FloatButtonProps, 'icon' | 'type'>> = (props) => (
  <FloatButton 
    type="primary" 
    tooltip="Add New"
    {...props} 
  />
);

export const BackToTopButton: React.FC<any> = (props) => (
  <FloatButtonBackTop
    {...props}
  />
);